import React, { createRef, useMemo, useCallback } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { CircleIcon, PrimaryButton, SquareIcon, TopBar } from "@kalyzee/kalyzee-ui";
import { LayoutType } from "../../interfaces/ui";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { toastError } from "../../helpers/toast";
import OverlayInformationMessage from "../utils/OverlayInformationMessage";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isExpert } from "../../helpers/roles";
import { HighlightButton } from "../highlight/button";
import { AppSoundSlider } from "../utils/AppSoundSlider";
import { MediaSourceViewer } from "./MediaSourceViewer";
import { MeetingSourceExtended } from "../../hooks/meeting";

import styles from "./BottomBar.module.css"

export const BottomBar = React.memo(({
  onPause,
  onStop,
  sources,
  showLayoutSelector,
}: {
  onUpdateMeetingState: (state: MeetingState) => void;
  onPause: () => void;
  onStop: () => void;
  sources: MeetingSourceExtended[];
  showLayoutSelector?: boolean;
}) => {
  const { containerAPI } = useMeetingContainerContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [audioPermitted] = useMeetingSelector(containerAPI, "session", session => session?.permissions?.audio ?? true);

  const layout = useAppStoreSelector(state => state.layout);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const sourcePickerOpen = useAppStoreSelector(state => state.sourcePickerOpen);

  const dispatch = useAppStoreDispatch();

  const handleCameraToggle = useCallback(async () => {
    if (!containerAPI) return;
    const manager = containerAPI.emissionManager;
    let success = true;
    if (manager.videoIsEnabled("cameras")) {
      success = (await containerAPI.disableVideo({ direction: "outgoing", group: "cameras" })).success;
    } else {
      success = (await containerAPI.enableVideo({ direction: "outgoing", group: "cameras" })).success;
    }
    if (!success) toastError("Une erreur est survenue...");
    dispatch("setVideoStreamEnabled", manager.videoIsEnabled("cameras"));
  }, [containerAPI, dispatch]);

  const handleAudioToggle = useCallback(async () => {
    if (!containerAPI) return;
    const manager = containerAPI.emissionManager;
    let success = true;
    if (manager.audioIsEnabled("cameras")) {
      success = (await containerAPI.mute({ direction: "outgoing", group: "cameras" })).success;
    } else {
      success = (await containerAPI.unmute({ direction: "outgoing", group: "cameras" })).success;
    }
    if (!success) toastError("Une erreur est survenue...");
    dispatch("setAudioStreamEnabled", manager.audioIsEnabled("cameras"));
  }, [containerAPI, dispatch]);

  const handleSourcePickerToggle = useCallback(() => {
    dispatch("setSourcePickerOpen", !sourcePickerOpen);
  }, [dispatch, sourcePickerOpen]);

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];
    
    if (showLayoutSelector) {
      items.push(
        <div className={styles.layoutSelector} key="layout-selector">
          <SquareIcon.Variants.HorizontalLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.HorizontalSquare}
            onClick={() => dispatch("setLayout", LayoutType.HorizontalSquare)}
          />
          <SquareIcon.Variants.VerticalLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.VerticalSquare}
            onClick={() => dispatch("setLayout", LayoutType.VerticalSquare)}
          />
          <SquareIcon.Variants.GridLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.Square}
            onClick={() => dispatch("setLayout", LayoutType.Square)}
          />
        </div>
      );
    }
    
    return items;
  }, [showLayoutSelector, layout, dispatch]);

  const renderCameraButton = useMemo(() => {
    const permitted = !(session?.permissions?.video === false);
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
    return (
      <div ref={currRef} key="student-dashboard-overlay-camera-btn">
        <PrimaryButton onClick={handleCameraToggle}>
          <CircleIcon.Variants.Camera
            unavailable={!permitted}
            disabled={!videoEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [session?.permissions?.video, videoEnabled, handleCameraToggle]);

  const renderAudioButton = useMemo(() => {
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
    return (
      <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
        <PrimaryButton onClick={handleAudioToggle}>
          <CircleIcon.Variants.Mic
            unavailable={!audioPermitted}
            disabled={!audioEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [audioPermitted, audioEnabled, handleAudioToggle]);
  
  const renderPauseButton = useMemo(() => (
    <PrimaryButton onClick={onPause} key="pause-btn">
      <CircleIcon.Variants.Pause />
    </PrimaryButton>
  ), [onPause]);

  const renderHangOffButton = useMemo(() => (
    <PrimaryButton onClick={onStop} key="hangoff-btn">
      <CircleIcon.Variants.HangOff
        style={{
          padding: "0.75em",
        }}
      />
    </PrimaryButton>
  ), [onStop]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (!isSpectator(session)) {
      if (isExpert(session)) {
        items.push(renderPauseButton);
        items.push(renderHangOffButton);
      }

      items.push(renderCameraButton);
      items.push(renderAudioButton);

      if (!isExpert(session)) {
        items.push(<HighlightButton key="highlight-btn" />);
      }
    }

    return items;
  }, [session, renderPauseButton, renderHangOffButton, renderCameraButton, renderAudioButton]);

  const right = useMemo(() => [
    <AppSoundSlider key="sound-slider" />,
    <MediaSourceViewer 
      key="media-source-viewer"
      sources={sources}
      meetingAPI={containerAPI}
      onClick={handleSourcePickerToggle}
      selected={sourcePickerOpen}
    />,
  ], [sources, containerAPI, handleSourcePickerToggle, sourcePickerOpen]);

  const style = useMemo(() => ({
    height: "unset",
    padding: "unset",
    backgroundColor: "transparent",
    boxShadow: "unset",
  }), []);

  if (!session) return null;
  
  return (
    <TopBar 
      style={style}
      left={left}
      center={center}
      right={right}
    />
  );
});